import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import {
    Award,
    ShieldCheck,
    Globe,
    Wine,
    Sparkles,
    ArrowRight,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80";

const PRINCIPLES = [
    {
        icon: Award,
        title: "Excellence",
        body: "We represent wineries with strong identities and exceptional products.",
    },
    {
        icon: ShieldCheck,
        title: "Trust",
        body: "Professional relationships begin with transparency and credibility.",
    },
    {
        icon: Globe,
        title: "International Expansion",
        body: "We help wineries reach new markets and opportunities.",
    },
    {
        icon: Wine,
        title: "Wine Heritage",
        body: "We respect the culture, history and traditions of wine.",
    },
    {
        icon: Sparkles,
        title: "Innovation",
        body: "Technology should strengthen business relationships, not replace them.",
    },
];

const WHY = [
    { title: "Premium Curation", body: "We focus on carefully selected wineries." },
    { title: "International Reach", body: "Designed for global trade opportunities." },
    { title: "Professional Profiles", body: "Detailed winery and wine presentations." },
    { title: "Commercial Connections", body: "Facilitating introductions between wineries and buyers." },
    { title: "Technology Driven", body: "Modern tools supporting international business growth." },
];

const STEPS = [
    "Selected wineries join CellarGlobal.",
    "Professional winery profiles are created.",
    "Wines are showcased internationally.",
    "Importers discover opportunities.",
    "Commercial relationships are established.",
];

export default function About() {
    return (
        <Layout transparentNav>
            {/* HERO */}
            <section className="relative h-[85vh] min-h-[600px] w-full overflow-hidden" data-testid="about-hero">
                <img
                    src={HERO_IMG}
                    alt="Premium vineyard at golden hour"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.2) 100%)" }} />
                <div className="absolute inset-x-0 bottom-0 h-56 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0))" }} />

                <div className="relative z-10 h-full flex items-center px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <p className="overline about-fade-up">About CellarGlobal</p>
                        <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] mt-6 leading-[0.95] about-fade-up" style={{ animationDelay: "120ms" }}>
                            Connecting Exceptional Wineries with
                            <em className="font-display-italic text-[#C9A961]"> Global Markets.</em>
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-[#E4E4E7] max-w-2xl leading-relaxed about-fade-up" style={{ animationDelay: "260ms" }}>
                            CellarGlobal is an international B2B platform designed to connect carefully selected wineries with importers, distributors and professional wine buyers around the world.
                        </p>
                    </div>
                </div>
            </section>

            {/* HISTORY */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="about-history">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <p className="overline">Our Story</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">A simple vision.</h2>
                    </div>
                    <div className="md:col-span-8 space-y-6 text-lg text-[#A1A1AA] leading-relaxed">
                        <p className="text-[#E4E4E7] font-display-italic text-2xl md:text-3xl leading-snug">
                            Exceptional wines deserve exceptional opportunities.
                        </p>
                        <p>
                            Around the world, many wineries produce remarkable wines with strong identities, unique terroirs and generations of expertise.
                        </p>
                        <p>
                            At the same time, importers constantly search for distinctive wines capable of enriching their portfolios and opening new commercial opportunities.
                        </p>
                        <p>CellarGlobal was created to bridge that gap.</p>
                        <p>
                            Our mission is to create a professional environment where wineries and international buyers can discover each other, build relationships and explore commercial opportunities.
                        </p>
                    </div>
                </div>
            </section>

            {/* MISSION + VISION */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="about-mission-vision">
                <div className="grid md:grid-cols-2 gap-12 md:gap-20">
                    <div className="border-l-2 border-[#C9A961] pl-8 md:pl-10">
                        <p className="overline">Mission</p>
                        <p className="font-display text-2xl md:text-3xl mt-6 leading-snug">
                            To connect selected wineries with international buyers through a premium digital platform that promotes quality, trust, innovation and long-term commercial relationships.
                        </p>
                    </div>
                    <div className="border-l-2 border-[#C9A961] pl-8 md:pl-10">
                        <p className="overline">Vision</p>
                        <p className="font-display text-2xl md:text-3xl mt-6 leading-snug">
                            To become the leading international platform connecting exceptional wineries with global wine markets.
                        </p>
                    </div>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="about-principles">
                <div className="max-w-3xl mb-20">
                    <p className="overline">Our Principles</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">Five values that guide us.</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
                    {PRINCIPLES.map(({ icon: Icon, title, body }, i) => (
                        <div key={title} className="border-t border-[#27272A] pt-8" data-testid={`about-principle-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                            <p className="text-[10px] tracking-[0.35em] uppercase text-[#C9A961] mb-6">{String(i + 1).padStart(2, "0")}</p>
                            <Icon className="w-8 h-8 text-[#C9A961]" strokeWidth={1.25} />
                            <h3 className="font-display text-2xl md:text-3xl mt-6">{title}</h3>
                            <p className="text-base text-[#A1A1AA] mt-4 leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* WHY CELLARGLOBAL */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="about-why">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <p className="overline">Why CellarGlobal</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">The difference.</h2>
                    </div>
                    <ul className="md:col-span-8 divide-y divide-[#27272A]">
                        {WHY.map(({ title, body }, i) => (
                            <li key={title} className="py-6 grid md:grid-cols-12 gap-6 items-baseline" data-testid={`about-why-${i}`}>
                                <div className="md:col-span-4">
                                    <p className="text-[10px] tracking-[0.35em] uppercase text-[#C9A961] mb-2">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="font-display text-2xl md:text-3xl">{title}</h3>
                                </div>
                                <p className="md:col-span-8 text-lg text-[#A1A1AA] leading-relaxed">{body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* FOR WINERIES + FOR IMPORTERS */}
            <section className="border-t border-[#27272A]">
                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#27272A]">
                    {/* Wineries */}
                    <div className="p-10 md:p-16 lg:p-24" data-testid="about-for-wineries">
                        <p className="overline">For Wineries</p>
                        <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">Expand Your Winery Beyond Borders.</h2>
                        <ul className="mt-10 space-y-4 text-base md:text-lg text-[#A1A1AA]">
                            {[
                                "Present your winery professionally.",
                                "Showcase your best wines.",
                                "Increase international visibility.",
                                "Connect with qualified buyers.",
                                "Generate export opportunities.",
                                "Build long-term relationships.",
                            ].map((line) => (
                                <li key={line} className="flex gap-3 items-start">
                                    <span className="w-1 h-1 rounded-full bg-[#C9A961] mt-3 shrink-0" />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/register" className="btn-gold hero-cta-glow mt-10 inline-flex" data-testid="about-cta-apply">
                            Apply for Selection <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    {/* Importers */}
                    <div className="p-10 md:p-16 lg:p-24 bg-[#050505]" data-testid="about-for-importers">
                        <p className="overline">For Importers</p>
                        <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">Discover Exceptional Wines Worldwide.</h2>
                        <ul className="mt-10 space-y-4 text-base md:text-lg text-[#A1A1AA]">
                            {[
                                "Explore selected wineries.",
                                "Access detailed wine information.",
                                "Connect directly with producers.",
                                "Discover new opportunities.",
                                "Expand your portfolio.",
                            ].map((line) => (
                                <li key={line} className="flex gap-3 items-start">
                                    <span className="w-1 h-1 rounded-full bg-[#C9A961] mt-3 shrink-0" />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/wineries" className="btn-outline mt-10 inline-flex" data-testid="about-cta-explore">
                            Explore Wineries <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="about-how-it-works">
                <div className="max-w-3xl mb-16">
                    <p className="overline">How It Works</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">Five steps to global reach.</h2>
                </div>
                <ol className="grid md:grid-cols-5 gap-8 md:gap-4">
                    {STEPS.map((step, i) => (
                        <li key={step} className="relative pt-10" data-testid={`about-step-${i + 1}`}>
                            <div className="absolute top-0 left-0 right-0 h-px bg-[#27272A]">
                                <div className="h-px bg-[#C9A961]" style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} />
                            </div>
                            <p className="text-[10px] tracking-[0.35em] uppercase text-[#C9A961]">Step {String(i + 1).padStart(2, "0")}</p>
                            <p className="mt-6 text-base md:text-lg text-[#E4E4E7] leading-relaxed">{step}</p>
                        </li>
                    ))}
                </ol>
            </section>

            {/* TRUST & TRANSPARENCY */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A] text-center" data-testid="about-trust">
                <div className="max-w-3xl mx-auto">
                    <p className="overline">Trust &amp; Transparency</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
                        Built for <em className="font-display-italic text-[#C9A961]">Professional</em> Relationships.
                    </h2>
                    <div className="mt-10 space-y-6 text-lg text-[#A1A1AA] leading-relaxed">
                        <p>
                            CellarGlobal is designed to support professional interactions between wineries and international buyers.
                        </p>
                        <p>Every winery profile is reviewed before publication.</p>
                        <p>
                            Our objective is to maintain a high-quality environment focused on credibility, transparency and long-term commercial opportunities.
                        </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-4 mt-12">
                        <Link to="/register" className="btn-gold hero-cta-glow" data-testid="about-trust-cta-apply">
                            Apply for Selection <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/wineries" className="btn-outline" data-testid="about-trust-cta-explore">
                            Explore Wineries
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
